import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "../components/ui/dialog";
import { Plus, Trash2, Pencil, Download, X } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const PAYMENT_TYPES = ["Cash", "UPI", "Card", "Online"];

function todayISO() {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 10);
}

function formatINR(n) {
  return "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });
}

export default function SalesPage() {
  const { user } = useAuth();
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [exporting, setExporting] = useState(false);
  const emptyForm = { amount: "", payment_type: "Cash", notes: "", date: todayISO() };
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      const { data } = await api.get("/sales", { params });
      setSales(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [startDate, endDate]);

  const openAdd = () => {
    setEditingId(null);
    setForm(emptyForm);
    setOpen(true);
  };

  const openEdit = (s) => {
    setEditingId(s.id);
    setForm({
      amount: String(s.amount),
      payment_type: s.payment_type,
      notes: s.notes || "",
      date: (s.date || "").slice(0, 10) || todayISO(),
    });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.amount || Number(form.amount) <= 0) {
      toast.error("Enter a valid amount");
      return;
    }
    setSubmitting(true);
    try {
      const payload = { ...form, amount: Number(form.amount) };
      if (editingId) {
        await api.patch(`/sales/${editingId}`, payload);
        toast.success("Sale updated");
      } else {
        await api.post("/sales", payload);
        toast.success("Sale added");
      }
      setForm(emptyForm);
      setEditingId(null);
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to save sale");
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this sale?")) return;
    try {
      await api.delete(`/sales/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to delete");
    }
  };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const params = {};
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      const res = await api.get("/sales/export", { params, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = `sales_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Exported CSV");
    } catch (e) {
      toast.error("Failed to export");
    } finally {
      setExporting(false);
    }
  };

  const clearDates = () => { setStartDate(""); setEndDate(""); };

  const canModify = (s) => user?.role === "admin" || s.created_by === user?.id;
  const total = sales.reduce((s, x) => s + x.amount, 0);

  return (
    <div className="p-6 sm:p-10 max-w-[1400px] mx-auto animate-fade-in-up" data-testid="sales-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-taupe-500 mb-2">Revenue log</div>
          <h1 className="font-heading font-extrabold text-3xl sm:text-4xl tracking-tighter text-espresso-900">
            Sales
          </h1>
          <p className="text-taupe-600 mt-2 text-sm">
            {sales.length} entries · <span className="font-semibold text-espresso-800">{formatINR(total)}</span> total
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            onClick={exportCsv}
            disabled={exporting || sales.length === 0}
            variant="outline"
            className="rounded-xl border-taupe-200 hover:border-espresso-800 hover:bg-taupe-100 text-espresso-800 font-semibold h-11"
            data-testid="export-sales-button"
          >
            <Download className="w-4 h-4 mr-2" /> {exporting ? "Exporting…" : "Export CSV"}
          </Button>

          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditingId(null); setForm(emptyForm); } }}>
            <DialogTrigger asChild>
              <Button
                type="button"
                onClick={openAdd}
                className="rounded-xl bg-espresso-800 hover:bg-espresso-900 text-white font-semibold px-6 h-11"
                data-testid="add-sale-button"
              >
                <Plus className="w-4 h-4 mr-2" /> Add sale
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md rounded-2xl">
              <DialogHeader>
                <DialogTitle className="font-heading text-2xl tracking-tight text-espresso-900">
                  {editingId ? "Edit sale" : "Add sale"}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={submit} className="space-y-4" data-testid="sale-form">
              <div>
                <Label className="text-sm font-semibold text-espresso-800 mb-2 block">Amount (₹)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.amount}
                  onChange={(e) => setForm({ ...form, amount: e.target.value })}
                  required
                  className="rounded-xl h-11 bg-taupe-100 border-taupe-200"
                  data-testid="sale-amount-input"
                />
              </div>
              <div>
                <Label className="text-sm font-semibold text-espresso-800 mb-2 block">Payment type</Label>
                <Select value={form.payment_type} onValueChange={(v) => setForm({ ...form, payment_type: v })}>
                  <SelectTrigger className="rounded-xl h-11 bg-taupe-100 border-taupe-200" data-testid="sale-payment-type-trigger">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PAYMENT_TYPES.map((p) => (
                      <SelectItem key={p} value={p} data-testid={`sale-payment-option-${p.toLowerCase()}`}>{p}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm font-semibold text-espresso-800 mb-2 block">Date</Label>
                <Input
                  type="date"
                  value={form.date}
                  onChange={(e) => setForm({ ...form, date: e.target.value })}
                  className="rounded-xl h-11 bg-taupe-100 border-taupe-200"
                  data-testid="sale-date-input"
                />
              </div>
              <div>
                <Label className="text-sm font-semibold text-espresso-800 mb-2 block">Description / notes</Label>
                <Textarea
                  rows={3}
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  placeholder="e.g. Cappuccino + Sandwich"
                  className="rounded-xl bg-taupe-100 border-taupe-200"
                  data-testid="sale-notes-input"
                />
              </div>
              <DialogFooter>
                <Button
                  type="submit"
                  disabled={submitting}
                  className="rounded-xl bg-espresso-800 hover:bg-espresso-900 text-white font-semibold"
                  data-testid="submit-sale-button"
                >
                  {submitting ? "Saving…" : editingId ? "Update sale" : "Save sale"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Date range filter */}
      <div className="card-soft p-4 sm:p-5 mb-6 flex flex-wrap items-end gap-3" data-testid="sales-filters">
        <div className="flex-1 min-w-[160px]">
          <Label className="text-xs font-semibold text-taupe-600 mb-1.5 block uppercase tracking-wider">From</Label>
          <Input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="rounded-xl h-11 bg-taupe-100 border-taupe-200"
            data-testid="sales-start-date"
          />
        </div>
        <div className="flex-1 min-w-[160px]">
          <Label className="text-xs font-semibold text-taupe-600 mb-1.5 block uppercase tracking-wider">To</Label>
          <Input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="rounded-xl h-11 bg-taupe-100 border-taupe-200"
            data-testid="sales-end-date"
          />
        </div>
        {(startDate || endDate) && (
          <Button
            type="button"
            variant="ghost"
            onClick={clearDates}
            className="rounded-xl h-11 text-taupe-600 hover:text-espresso-800"
            data-testid="clear-sales-filter"
          >
            <X className="w-4 h-4 mr-1" /> Clear
          </Button>
        )}
      </div>

      <div className="card-soft overflow-hidden" data-testid="sales-table-card">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-taupe-100 text-taupe-600 text-xs font-semibold uppercase tracking-wider">
                <th className="text-left py-4 px-6">Date</th>
                <th className="text-left py-4 px-6">Payment</th>
                <th className="text-left py-4 px-6">Notes</th>
                <th className="text-right py-4 px-6">Amount</th>
                <th className="text-right py-4 px-6" />
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={5} className="py-8 text-center text-taupe-500 text-sm">Loading…</td></tr>
              )}
              {!loading && sales.length === 0 && (
                <tr><td colSpan={5} className="py-12 text-center text-taupe-500 text-sm" data-testid="sales-empty">
                  No sales yet. Add your first entry.
                </td></tr>
              )}
              {sales.map((s) => (
                <tr key={s.id} className="border-t border-taupe-200 hover:bg-taupe-100/40 transition-colors" data-testid="sales-row">
                  <td className="py-4 px-6 text-sm text-espresso-900">{new Date(s.date).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}</td>
                  <td className="py-4 px-6 text-sm">
                    <span className="inline-flex px-2.5 py-1 rounded-md bg-espresso-100 text-espresso-800 text-xs font-semibold">{s.payment_type}</span>
                  </td>
                  <td className="py-4 px-6 text-sm text-taupe-700 max-w-[300px] truncate">{s.notes || <span className="text-taupe-500">—</span>}</td>
                  <td className="py-4 px-6 text-right font-semibold text-espresso-900">{formatINR(s.amount)}</td>
                  <td className="py-4 px-6 text-right">
                    <div className="inline-flex items-center gap-1">
                      <button
                        onClick={() => openEdit(s)}
                        disabled={!canModify(s)}
                        className="p-2 rounded-lg text-espresso-800 hover:bg-taupe-100 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                        data-testid={`edit-sale-${s.id}`}
                        aria-label="Edit"
                        title={canModify(s) ? "Edit" : "Only owner or admin can edit"}
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => remove(s.id)}
                        disabled={!canModify(s)}
                        className="p-2 rounded-lg text-terracotta-500 hover:bg-terracotta-100 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                        data-testid={`delete-sale-${s.id}`}
                        aria-label="Delete"
                        title={canModify(s) ? "Delete" : "Only owner or admin can delete"}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
